import { useEffect, useMemo, useRef, useState } from "react";
import type { PublicRelease, PublicReleaseImage } from "../api/types";
import { ReleaseImage } from "./ReleaseImage";

type ReleaseImageGalleryProps = {
  release: PublicRelease;
  selectedImageId: string | null;
  onSelect: (imageId: string) => void;
  onReplaceUserImage?: (imageId: string) => void;
  onDeleteUserImage?: (imageId: string) => void;
};

function toPrimaryLike(image: PublicReleaseImage) {
  return {
    originalUrl: image.url,
    thumbnailUrl: image.thumbnailUrl,
    width: image.width,
    height: image.height,
  };
}

export function ReleaseImageGallery({
  release,
  selectedImageId,
  onSelect,
  onReplaceUserImage,
  onDeleteUserImage,
}: ReleaseImageGalleryProps) {
  const images = useMemo(() => release.images ?? [], [release.images]);
  const selected = images.find((image) => image.imageId === selectedImageId) ??
    images.find((image) => image.isPrimary) ??
    null;
  const display = selected
    ? toPrimaryLike(selected)
    : release.primaryImage;

  const canManage = Boolean(onReplaceUserImage && onDeleteUserImage);

  return (
    <div className="hw-release-gallery">
      <div className="hw-release-gallery__main">
        <ReleaseImage
          image={display}
          alt={release.casting.name}
          priority
          size="detail"
          className="hw-release-detail__image"
        />
        {canManage && selected?.source === "USER_UPLOAD" && selected.ownedByCurrentUser ? (
          <UserPhotoActions
            imageId={selected.imageId}
            onReplaceUserImage={onReplaceUserImage!}
            onDeleteUserImage={onDeleteUserImage!}
          />
        ) : null}
      </div>
      {selected?.source === "USER_UPLOAD" ? (
        <p className="hw-release-gallery__badge">Пользовательское фото</p>
      ) : null}
      {images.length > 1 ? (
        <ul className="hw-release-gallery__thumbs">
          {images.map((image) => (
            <li key={image.imageId}>
              {canManage && image.source === "USER_UPLOAD" && image.ownedByCurrentUser ? (
                <UserPhotoActions
                  imageId={image.imageId}
                  onReplaceUserImage={onReplaceUserImage!}
                  onDeleteUserImage={onDeleteUserImage!}
                  compact
                />
              ) : null}
              <button
                type="button"
                className={
                  image.imageId === selected?.imageId
                    ? "hw-release-gallery__thumb is-selected"
                    : "hw-release-gallery__thumb"
                }
                onClick={() => onSelect(image.imageId)}
                aria-label="Показать фото"
              >
                <img src={image.thumbnailUrl} alt="" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function UserPhotoActions({
  imageId,
  onReplaceUserImage,
  onDeleteUserImage,
  compact = false,
}: {
  imageId: string;
  onReplaceUserImage: (imageId: string) => void;
  onDeleteUserImage: (imageId: string) => void;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }
    function onPointer(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  return (
    <div
      className={
        compact
          ? "hw-photo-actions hw-photo-actions--compact"
          : "hw-photo-actions"
      }
      ref={rootRef}
    >
      <button
        type="button"
        className="hw-overflow__trigger hw-photo-actions__trigger"
        aria-label="Действия пользовательского фото"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        ⋯
      </button>
      {open ? (
        <div className="hw-overflow__menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="hw-overflow__item"
            onClick={() => {
              setOpen(false);
              onReplaceUserImage(imageId);
            }}
          >
            Заменить фото
          </button>
          <button
            type="button"
            role="menuitem"
            className="hw-overflow__item"
            onClick={() => {
              setOpen(false);
              onDeleteUserImage(imageId);
            }}
          >
            Удалить фото
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function initialSelectedImageId(release: PublicRelease): string | null {
  return release.images?.find((image) => image.isPrimary)?.imageId ??
    release.images?.[0]?.imageId ??
    null;
}

export function useSelectedReleaseImage(release: PublicRelease | null) {
  const [selectedImageId, setSelectedImageId] = useState<string | null>(
    release ? initialSelectedImageId(release) : null,
  );

  useEffect(() => {
    if (!release) {
      setSelectedImageId(null);
      return;
    }
    setSelectedImageId((current) => {
      if (current && release.images?.some((image) => image.imageId === current)) {
        return current;
      }
      return initialSelectedImageId(release);
    });
  }, [release]);

  return { selectedImageId, setSelectedImageId };
}
